import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertCircle,
  Webhook,
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  Activity,
  Clock,
  Loader2,
  RefreshCw,
  MoreHorizontal,
  TestTube,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  automationApi,
  WebhookConfig,
  WebhookConfigCreate,
  WebhookConfigUpdate,
  WebhookLogEntry,
} from "@/services/api/automation";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import { useSortableData } from "@/hooks/useSortableData";
import { useClientPaginatedData } from "@/hooks/usePaginatedData";

export const Route = createFileRoute("/portal/automation/webhooks")({
  component: WebhooksPage,
});

interface WebhookFormData {
  name: string;
  serviceName: string;
  webhookUrl: string;
  events: string[];
  headers?: Record<string, string>;
  retryEnabled: boolean;
  maxRetries: number;
  timeoutSeconds: number;
  isActive: boolean;
}

const EVENT_TYPES = [
  { value: "ticket.created", label: "Ticket Created" },
  { value: "ticket.updated", label: "Ticket Updated" },
  { value: "ticket.resolved", label: "Ticket Resolved" },
  { value: "message.sent", label: "Message Sent" },
  { value: "message.received", label: "Message Received" },
  { value: "workflow.started", label: "Workflow Started" },
  { value: "workflow.completed", label: "Workflow Completed" },
  { value: "workflow.failed", label: "Workflow Failed" },
  { value: "ai.response", label: "AI Response Generated" },
  { value: "engagement.completed", label: "Engagement Completed" },
  { value: "credential.expired", label: "Credential Expired" },
];

const SERVICE_NAMES = [
  { value: "agentic", label: "Agentic (AI Workflows)" },
  { value: "messaging", label: "Messaging (WhatsApp/Email)" },
];

function WebhooksPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can } = usePermissions();
  const organisationId = selectedOrganisation?.organisationId;

  const canCreateWebhooks = can("create", "automation");
  const canEditWebhooks = can("edit", "automation");
  const canDeleteWebhooks = can("delete", "automation");

  const [webhooks, setWebhooks] = useState<WebhookConfig[]>([]);
  const [webhookLogs, setWebhookLogs] = useState<
    Record<string, WebhookLogEntry[]>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookConfig | null>(
    null,
  );
  const [testingWebhook, setTestingWebhook] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("webhooks");

  // Form state
  const [formData, setFormData] = useState<WebhookFormData>({
    name: "",
    serviceName: "agentic",
    webhookUrl: "",
    events: [],
    headers: {},
    retryEnabled: true,
    maxRetries: 3,
    timeoutSeconds: 30,
    isActive: true,
  });

  const [searchTerm, setSearchTerm] = useState("");

  // Headers form state
  const [headerKey, setHeaderKey] = useState("");
  const [headerValue, setHeaderValue] = useState("");

  useEffect(() => {
    if (token && organisationId) {
      loadWebhooks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, organisationId]);

  const loadWebhooks = async () => {
    if (!token || !organisationId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await automationApi.webhooks.list(token, organisationId);
      setWebhooks(response || []);
    } catch (err) {
      console.error("Failed to load webhooks:", err);
      setError("Failed to load webhooks");
    } finally {
      setIsLoading(false);
    }
  };

  const loadWebhookLogs = async (webhookId: string) => {
    if (!token || !organisationId) return;
    try {
      const response = await automationApi.webhooks.getLogs(
        token,
        organisationId,
        webhookId,
      );
      setWebhookLogs((prev) => ({ ...prev, [webhookId]: response || [] }));
    } catch (err) {
      console.error("Failed to load webhook logs:", err);
    }
  };

  const handleCreateWebhook = async () => {
    if (!token || !organisationId) return;
    setError(null);
    try {
      const createData: WebhookConfigCreate = {
        name: formData.name,
        serviceName: formData.serviceName,
        webhookUrl: formData.webhookUrl,
        events: formData.events.length > 0 ? formData.events : undefined,
        headers:
          Object.keys(formData.headers || {}).length > 0
            ? formData.headers
            : undefined,
        retryEnabled: formData.retryEnabled,
        maxRetries: formData.maxRetries,
        timeoutSeconds: formData.timeoutSeconds,
        isActive: formData.isActive,
      };
      await automationApi.webhooks.create(token, organisationId, createData);
      await loadWebhooks();
      setIsAddDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error("Failed to create webhook:", err);
      setError("Failed to create webhook");
    }
  };

  const handleUpdateWebhook = async () => {
    if (!token || !organisationId || !selectedWebhook) return;

    setError(null);
    try {
      const updateData: WebhookConfigUpdate = {
        name: formData.name,
        webhookUrl: formData.webhookUrl,
        events: formData.events.length > 0 ? formData.events : undefined,
        headers:
          Object.keys(formData.headers || {}).length > 0
            ? formData.headers
            : undefined,
        retryEnabled: formData.retryEnabled,
        maxRetries: formData.maxRetries,
        timeoutSeconds: formData.timeoutSeconds,
        isActive: formData.isActive,
      };
      await automationApi.webhooks.update(
        token,
        organisationId,
        selectedWebhook.id,
        updateData,
      );
      await loadWebhooks();
      setIsEditDialogOpen(false);
      setSelectedWebhook(null);
      resetForm();
    } catch (err) {
      console.error("Failed to update webhook:", err);
      setError("Failed to update webhook");
    }
  };

  const handleDeleteWebhook = async (webhookId: string) => {
    if (!token || !organisationId) return;
    if (!confirm("Are you sure you want to delete this webhook?")) return;

    setError(null);
    try {
      await automationApi.webhooks.delete(token, organisationId, webhookId);
      await loadWebhooks();
    } catch (err) {
      console.error("Failed to delete webhook:", err);
      setError("Failed to delete webhook");
    }
  };

  const handleTestWebhook = async (webhookId: string) => {
    if (!token || !organisationId) return;
    setTestingWebhook(webhookId);
    setError(null);
    try {
      const response = await automationApi.webhooks.test(
        token,
        organisationId,
        webhookId,
      );
      if (response.success) {
        // Show success briefly
        setTimeout(() => setTestingWebhook(null), 2000);
      } else {
        setError("Webhook test failed");
        setTestingWebhook(null);
      }
    } catch (err) {
      console.error("Failed to test webhook:", err);
      setError("Failed to test webhook");
      setTestingWebhook(null);
    }
  };

  const addHeader = () => {
    if (headerKey && headerValue) {
      setFormData({
        ...formData,
        headers: {
          ...formData.headers,
          [headerKey]: headerValue,
        },
      });
      setHeaderKey("");
      setHeaderValue("");
    }
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...formData.headers };
    delete newHeaders[key];
    setFormData({ ...formData, headers: newHeaders });
  };

  const toggleEvent = (eventValue: string) => {
    const newEvents = formData.events.includes(eventValue)
      ? formData.events.filter((e) => e !== eventValue)
      : [...formData.events, eventValue];
    setFormData({ ...formData, events: newEvents });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      serviceName: "agentic",
      webhookUrl: "",
      events: [],
      headers: {},
      retryEnabled: true,
      maxRetries: 3,
      timeoutSeconds: 30,
      isActive: true,
    });
    setHeaderKey("");
    setHeaderValue("");
  };

  const openEditDialog = (webhook: WebhookConfig) => {
    setSelectedWebhook(webhook);
    setFormData({
      name: webhook.name,
      serviceName: webhook.serviceName,
      webhookUrl: webhook.webhookUrl,
      events: webhook.events || [],
      headers: webhook.headers || {},
      retryEnabled: webhook.retryEnabled,
      maxRetries: webhook.maxRetries,
      timeoutSeconds: webhook.timeoutSeconds,
      isActive: webhook.isActive,
    });
    setIsEditDialogOpen(true);
  };

  // Sort + paginate webhooks
  const { sortedData: sortedWebhooks, requestSort, getSortDirection } =
    useSortableData(webhooks, {
      initialSort: { key: "name", direction: "asc" },
    });

  const {
    paginatedData,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
  } = useClientPaginatedData({
    data: sortedWebhooks as unknown as Record<string, unknown>[],
    itemsPerPage: 25,
    searchFields: ["name", "serviceName", "webhookUrl"],
    searchTerm,
  });
  const displayWebhooks = paginatedData as unknown as WebhookConfig[];

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const renderWebhookForm = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Webhook Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., My CRM Webhook"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="service">Service</Label>
          <Select
            value={formData.serviceName}
            onValueChange={(value) =>
              setFormData({ ...formData, serviceName: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_NAMES.map((service) => (
                <SelectItem key={service.value} value={service.value}>
                  {service.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="url">Webhook URL</Label>
        <Input
          id="url"
          type="url"
          value={formData.webhookUrl}
          onChange={(e) =>
            setFormData({ ...formData, webhookUrl: e.target.value })
          }
          placeholder="https://api.example.com/webhook"
        />
      </div>

      <div className="space-y-2">
        <Label>Event Types (leave empty for all events)</Label>
        <div className="grid grid-cols-2 gap-2">
          {EVENT_TYPES.map((type) => (
            <div key={type.value} className="flex items-center space-x-2">
              <input
                type="checkbox"
                id={`event-${type.value}`}
                checked={formData.events.includes(type.value)}
                onChange={() => toggleEvent(type.value)}
                className="h-4 w-4"
              />
              <Label htmlFor={`event-${type.value}`} className="text-sm">
                {type.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="timeout">Timeout (seconds)</Label>
          <Input
            id="timeout"
            type="number"
            value={formData.timeoutSeconds}
            onChange={(e) =>
              setFormData({
                ...formData,
                timeoutSeconds: parseInt(e.target.value),
              })
            }
            min="1"
            max="120"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="max-retries">Max Retries</Label>
          <Input
            id="max-retries"
            type="number"
            value={formData.maxRetries}
            onChange={(e) =>
              setFormData({
                ...formData,
                maxRetries: parseInt(e.target.value),
              })
            }
            min="0"
            max="10"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Custom Headers</Label>
        <div className="space-y-2">
          <div className="flex space-x-2">
            <Input
              placeholder="Header name"
              value={headerKey}
              onChange={(e) => setHeaderKey(e.target.value)}
            />
            <Input
              placeholder="Header value"
              value={headerValue}
              onChange={(e) => setHeaderValue(e.target.value)}
            />
            <Button type="button" onClick={addHeader} variant="outline">
              Add
            </Button>
          </div>
          {Object.entries(formData.headers || {}).map(([key, value]) => (
            <div
              key={key}
              className="flex items-center justify-between p-2 bg-secondary rounded"
            >
              <span className="font-mono text-sm">
                {key}: {value}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeHeader(key)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="retry"
            checked={formData.retryEnabled}
            onChange={(e) =>
              setFormData({ ...formData, retryEnabled: e.target.checked })
            }
            className="h-4 w-4"
          />
          <Label htmlFor="retry">Enable automatic retries</Label>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="active"
            checked={formData.isActive}
            onChange={(e) =>
              setFormData({ ...formData, isActive: e.target.checked })
            }
            className="h-4 w-4"
          />
          <Label htmlFor="active">Webhook is active</Label>
        </div>
      </div>
    </div>
  );

  if (!organisationId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an organisation</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Webhooks</h1>
          <p className="mt-2 text-lg text-slate-600">
            Configure webhooks to receive real-time notifications for system
            events
          </p>
        </div>
        {canCreateWebhooks && (
          <Button className="rounded-full" onClick={() => { resetForm(); setIsAddDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Webhook
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="webhooks">Configured Webhooks</TabsTrigger>
          <TabsTrigger value="logs">Recent Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="webhooks">
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <SearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={() => setSearchTerm("")}
                placeholder="Search webhooks..."
                containerClassName="flex-1 min-w-[200px]"
              />
              <Button variant="outline" size="sm" onClick={loadWebhooks} disabled={isLoading}>
                {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Refresh
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : displayWebhooks.length === 0 ? (
              <div className="text-center py-12">
                <Webhook className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  {searchTerm ? "No webhooks match your search" : "No webhooks configured yet"}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm ? "Try a different search term." : "Add webhooks to receive real-time event notifications."}
                </p>
                {!searchTerm && canCreateWebhooks && (
                  <Button onClick={() => { resetForm(); setIsAddDialogOpen(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Webhook
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead direction={getSortDirection("name")} onSort={() => requestSort("name")}>Name</SortableTableHead>
                    <SortableTableHead direction={getSortDirection("serviceName")} onSort={() => requestSort("serviceName")}>Service</SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>URL</SortableTableHead>
                    <SortableTableHead direction={getSortDirection("isActive")} onSort={() => requestSort("isActive")}>Status</SortableTableHead>
                    <SortableTableHead direction={getSortDirection("maxRetries")} onSort={() => requestSort("maxRetries")}>Retries</SortableTableHead>
                    <SortableTableHead direction={getSortDirection("timeoutSeconds")} onSort={() => requestSort("timeoutSeconds")}>Timeout</SortableTableHead>
                    <SortableTableHead direction={getSortDirection("createdAt")} onSort={() => requestSort("createdAt")}>Created</SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">{" "}</SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayWebhooks.map((webhook) => (
                    <TableRow key={webhook.id}>
                      <TableCell className="font-medium">{webhook.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{webhook.serviceName}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[250px]">
                        <span className="text-sm font-mono text-muted-foreground truncate block">
                          {webhook.webhookUrl}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={webhook.isActive ? "default" : "secondary"}>
                          {webhook.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {webhook.retryEnabled ? webhook.maxRetries : "Off"}
                      </TableCell>
                      <TableCell className="text-sm">{webhook.timeoutSeconds}s</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {webhook.createdAt ? formatDate(webhook.createdAt) : "—"}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {testingWebhook === webhook.id ? (
                              <DropdownMenuItem disabled>
                                <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                                Test Passed
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleTestWebhook(webhook.id)}>
                                <TestTube className="h-4 w-4 mr-2" />
                                Test
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => { loadWebhookLogs(webhook.id); setActiveTab("logs"); }}>
                              <Activity className="h-4 w-4 mr-2" />
                              View Logs
                            </DropdownMenuItem>
                            {canEditWebhooks && (
                              <DropdownMenuItem onClick={() => openEditDialog(webhook)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                            )}
                            {canDeleteWebhooks && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDeleteWebhook(webhook.id)} className="text-destructive focus:text-destructive">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {totalItems > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  totalCount={totalItems}
                  pageSize={itemsPerPage}
                  pageSizeOptions={[10, 25, 50]}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={currentPage > 1}
                  canGoNext={currentPage < totalPages}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setItemsPerPage}
                  onFirstPage={() => setCurrentPage(1)}
                  onLastPage={() => setCurrentPage(totalPages)}
                  onPrevPage={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  onNextPage={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                />
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="logs">
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
            {Object.entries(webhookLogs).length === 0 ? (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No webhook activity yet</h3>
                <p className="text-muted-foreground">
                  Select a webhook and click "View Logs" to see delivery history.
                </p>
              </div>
            ) : (
              Object.entries(webhookLogs).map(([webhookId, logs]) => (
                <div key={webhookId} className="space-y-2">
                  <h4 className="font-medium">
                    {webhooks.find((w) => w.id === webhookId)?.name || webhookId}
                  </h4>
                  {logs.map((log, index: number) => (
                    <div key={index} className="border rounded p-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-mono">{log.eventType}</span>
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            log.success
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {log.responseStatusCode || (log.success ? "200" : "Failed")}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground mt-1">
                        {log.createdAt && (
                          <span>{new Date(log.createdAt).toLocaleString()}</span>
                        )}
                        {log.deliveryDurationMs && (
                          <span>{log.deliveryDurationMs}ms</span>
                        )}
                        {log.retryCount > 0 && (
                          <span>Retries: {log.retryCount}</span>
                        )}
                      </div>
                      {log.errorMessage && (
                        <p className="text-xs text-red-600 mt-1">{log.errorMessage}</p>
                      )}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Webhook</DialogTitle>
            <DialogDescription>
              Configure a webhook endpoint to receive event notifications
            </DialogDescription>
          </DialogHeader>
          {renderWebhookForm()}
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateWebhook}>Create Webhook</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Webhook</DialogTitle>
            <DialogDescription>Update webhook configuration</DialogDescription>
          </DialogHeader>
          {renderWebhookForm()}
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateWebhook}>Update Webhook</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
